import ApiHelper from './index';

// ==================== Customer Portal APIs ====================

/**
 * Get license summary for current organization
 * @param {boolean} spinner - Show loading spinner
 * @returns {Promise<[Error|null, LicenseSummary]>}
 */
const getLicenseSummary = async (spinner = true) => {
  return await ApiHelper.get('licensing/portal/summary/', spinner);
};

/**
 * Get devices with optional status filter
 * @param {Object} queryParams - { status: 'assigned' | 'unassigned' | 'exempt' }
 * @param {boolean} spinner - Show loading spinner
 * @returns {Promise<[Error|null, Device[]]>}
 */
const getDevices = async (queryParams = {}, spinner = true) => {
  return await ApiHelper.get('licensing/portal/devices/', spinner, queryParams);
};

/**
 * Assign license to a device
 * @param {number} deviceId - Device ID
 * @param {boolean} spinner - Show loading spinner
 * @returns {Promise<[Error|null, AssignResponse]>}
 */
const assignLicense = async (deviceId, spinner = true) => {
  return await ApiHelper.post(
    `licensing/portal/devices/${deviceId}/assign/`,
    {},
    spinner
  );
};

/**
 * Revoke license from a device
 * @param {number} deviceId - Device ID
 * @param {boolean} spinner - Show loading spinner
 * @returns {Promise<[Error|null, RevokeResponse]>}
 */
const revokeLicense = async (deviceId, spinner = true) => {
  return await ApiHelper.post(
    `licensing/portal/devices/${deviceId}/revoke/`,
    {},
    spinner
  );
};

/**
 * Get license event history
 * @param {Object} queryParams - { event_type?: string, device_id?: number }
 * @param {boolean} spinner - Show loading spinner
 * @returns {Promise<[Error|null, LicenseEvent[]]>}
 */
const getLicenseEvents = async (queryParams = {}, spinner = true) => {
  return await ApiHelper.get('licensing/portal/events/', spinner, queryParams);
};

// ==================== Admin Portal APIs ====================

/**
 * Get license info for an organization
 * @param {string} orgId - Organization name/ID
 * @param {boolean} spinner - Show loading spinner
 * @returns {Promise<[Error|null, OrgLicenseInfo]>}
 */
const getOrgLicenses = async (orgId, spinner = true) => {
  return await ApiHelper.get(`licensing/admin/orgs/${orgId}/licenses/`, spinner);
};

/**
 * Update license count for an organization (POST or PUT)
 * @param {string} orgId - Organization name/ID
 * @param {Object} payload - { total_licenses: number }
 * @param {boolean} spinner - Show loading spinner
 * @returns {Promise<[Error|null, OrgLicenseInfo]>}
 */
const updateOrgLicenses = async (orgId, payload, spinner = true) => {
  return await ApiHelper.post(
    `licensing/admin/orgs/${orgId}/licenses/`,
    payload,
    spinner
  );
};

/**
 * Get devices for an organization (Admin)
 * @param {string} orgId - Organization name/ID
 * @param {Object} queryParams - { status: 'assigned' | 'unassigned' | 'exempt' }
 * @param {boolean} spinner - Show loading spinner
 * @returns {Promise<[Error|null, Device[]]>}
 */
const getAdminOrgDevices = async (orgId, queryParams = {}, spinner = true) => {
  return await ApiHelper.get(
    `licensing/admin/orgs/${orgId}/devices/`,
    spinner,
    queryParams
  );
};

/**
 * Assign license to a device (Admin)
 * @param {string} orgId - Organization name/ID
 * @param {number} deviceId - Device ID
 * @param {boolean} spinner - Show loading spinner
 * @returns {Promise<[Error|null, AssignResponse]>}
 */
const adminAssignLicense = async (orgId, deviceId, spinner = true) => {
  return await ApiHelper.post(
    `licensing/admin/orgs/${orgId}/devices/${deviceId}/assign/`,
    {},
    spinner
  );
};

/**
 * Revoke license from a device (Admin)
 * @param {string} orgId - Organization name/ID
 * @param {number} deviceId - Device ID
 * @param {boolean} spinner - Show loading spinner
 * @returns {Promise<[Error|null, RevokeResponse]>}
 */
const adminRevokeLicense = async (orgId, deviceId, spinner = true) => {
  return await ApiHelper.post(
    `licensing/admin/orgs/${orgId}/devices/${deviceId}/revoke/`,
    {},
    spinner
  );
};

/**
 * Get license events for an organization (Admin)
 * @param {string} orgId - Organization name/ID
 * @param {Object} queryParams - { event_type?: string }
 * @param {boolean} spinner - Show loading spinner
 * @returns {Promise<[Error|null, LicenseEvent[]]>}
 */
const getAdminLicenseEvents = async (orgId, queryParams = {}, spinner = true) => {
  return await ApiHelper.get(
    `licensing/admin/orgs/${orgId}/events/`,
    spinner,
    queryParams
  );
};

export default {
  // Customer Portal
  getLicenseSummary,
  getDevices,
  assignLicense,
  revokeLicense,
  getLicenseEvents,
  // Admin Portal
  getOrgLicenses,
  updateOrgLicenses,
  getAdminOrgDevices,
  adminAssignLicense,
  adminRevokeLicense,
  getAdminLicenseEvents,
};
